/**
 * Broker Dashboard Mock Data
 * 
 * Comprehensive mock data for all Broker Dashboard features.
 * Used during Phase 1 frontend development.
 * 
 * Structure: Properties contain units. Units can be OPEN or FUNDED.
 */

// ============================================================================
// BROKER PROPERTIES & UNITS MOCKS
// ============================================================================

export const MOCK_BROKER_PROPERTIES = [
  {
    projectId: 'PROJ_001',
    name: 'Skyline Heights',
    location: 'Sector 45',
    city: 'Gurgaon',
    type: 'residential',
    units: [
      {
        unitId: 'UNIT_SKY_001',
        unitName: 'A-101',
        status: 'OPEN', // Available for investment
        financial: {
          unitValue: 16666667,
          capitalTarget: 5000000,
          capitalRaised: 3500000,
          minInvestment: 500000,
        },
        specifications: { size: '1850 sq ft', floor: '10th' },
        llp: { name: 'Skyline A101 LLP' },
        maxDistributable: 2.5,
        daysLeft: 30,
        totalSlots: 10,
        filledSlots: 7,
      },
      {
        unitId: 'UNIT_SKY_002',
        unitName: 'A-102',
        status: 'FUNDED', // Fully funded - not available
        financial: {
          unitValue: 16666667,
          capitalTarget: 5000000,
          capitalRaised: 5000000,
          minInvestment: 500000,
        },
        specifications: { size: '1850 sq ft', floor: '10th' },
        llp: { name: 'Skyline A102 LLP' },
        maxDistributable: 2.5,
        closedAt: '2024-11-15',
      },
      {
        unitId: 'UNIT_SKY_003',
        unitName: 'A-103',
        status: 'FUNDED', // Fully funded
        financial: {
          unitValue: 20000000,
          capitalTarget: 6000000,
          capitalRaised: 6000000,
          minInvestment: 500000,
        },
        specifications: { size: '2200 sq ft', floor: '11th' },
        llp: { name: 'Skyline A103 LLP' },
        maxDistributable: 2.5,
        closedAt: '2024-10-20',
      },
    ],
  },
  {
    projectId: 'PROJ_002',
    name: 'Green Valley',
    location: 'Whitefield',
    city: 'Bangalore',
    type: 'land',
    units: [
      {
        unitId: 'UNIT_GRN_001',
        unitName: 'Villa 12',
        status: 'OPEN',
        financial: {
          unitValue: 26666667,
          capitalTarget: 8000000,
          capitalRaised: 4000000,
          minInvestment: 500000,
        },
        specifications: { size: '3200 sq ft', floor: 'Ground' },
        llp: { name: 'GreenVal V12 LLP' },
        maxDistributable: 2.0,
        daysLeft: 45,
        totalSlots: 16,
        filledSlots: 6,
      },
      {
        unitId: 'UNIT_GRN_002',
        unitName: 'Villa 08',
        status: 'FUNDED',
        financial: {
          unitValue: 23333334,
          capitalTarget: 7000000,
          capitalRaised: 7000000,
          minInvestment: 500000,
        },
        specifications: { size: '2800 sq ft', floor: 'Ground' },
        llp: { name: 'GreenVal V08 LLP' },
        maxDistributable: 2.0,
        closedAt: '2024-09-05',
      },
    ],
  },
  {
    projectId: 'PROJ_003',
    name: 'Metro Square',
    location: 'Andheri East',
    city: 'Mumbai',
    type: 'commercial',
    units: [
      {
        unitId: 'UNIT_MET_001',
        unitName: 'Office 301',
        status: 'OPEN',
        financial: {
          unitValue: 33333334,
          capitalTarget: 10000000,
          capitalRaised: 2500000,
          minInvestment: 1000000,
        },
        specifications: { size: '2500 sq ft', floor: '3rd' },
        llp: { name: 'Metro 301 LLP' },
        maxDistributable: 3.0,
        daysLeft: 60,
        totalSlots: 10,
        filledSlots: 2,
      },
      {
        unitId: 'UNIT_MET_002',
        unitName: 'Office 302',
        status: 'OPEN',
        financial: {
          unitValue: 30000000,
          capitalTarget: 9000000,
          capitalRaised: 1500000,
          minInvestment: 1000000,
        },
        specifications: { size: '2200 sq ft', floor: '3rd' },
        llp: { name: 'Metro 302 LLP' },
        maxDistributable: 3.0,
        daysLeft: 60,
        totalSlots: 9,
        filledSlots: 1,
      },
    ],
  },
];

// Helper to get summary stats for a property
export const getPropertySummary = (property) => {
  const openUnits = property.units.filter(u => u.status === 'OPEN');
  const fundedUnits = property.units.filter(u => u.status === 'FUNDED');
  
  return {
    totalUnits: property.units.length,
    openUnits: openUnits.length,
    fundedUnits: fundedUnits.length,
    totalCapitalTarget: property.units.reduce((sum, u) => sum + (u.financial?.capitalTarget || 0), 0),
    totalCapitalRaised: property.units.reduce((sum, u) => sum + (u.financial?.capitalRaised || 0), 0),
  };
};

// ============================================================================
// BROKER STATS MOCK
// ============================================================================

export const MOCK_BROKER_STATS = {
  totalRaised: 4500000,
  lifetimeRaised: 4500000,
  totalCommission: 157500,
  pendingCommission: 45000,
  activeInvestors: 8,
  dealsContributed: 3,
  rank: 12,
  tier: null,
};

// ============================================================================
// BROKER PIPELINE MOCK
// ============================================================================

export const MOCK_BROKER_PIPELINE = {
  funnel: {
    PENDING: { count: 2, amount: 1750000 },
    CLICKED: { count: 2, amount: 3000000 },
    REGISTERED: { count: 1, amount: 500000 },
    KYC_DONE: { count: 1, amount: 2000000 },
    EOI_PAID: { count: 2, amount: 3000000 },
    CONVERTED: { count: 2, amount: 3000000 },
    EXPIRED: { count: 0, amount: 0 },
    CANCELLED: { count: 0, amount: 0 },
  },
  assignments: [
    { 
      assignmentId: 'ASN_001', 
      investorName: 'Arun Sharma', 
      investorPhone: '+91 98765 11111', 
      investorEmail: 'arun@gmail.com',
      status: 'PENDING', 
      propertyName: 'Skyline Heights',
      unitName: 'Unit A-101',
      amount: 1000000,
      createdAt: '2024-12-10T10:30:00Z',
      expiresAt: '2024-12-17T10:30:00Z',
      daysInStage: 2
    },
    { 
      assignmentId: 'ASN_002', 
      investorName: 'Meena Kumari', 
      investorPhone: '+91 98765 22222', 
      investorEmail: 'meena@gmail.com',
      status: 'CLICKED', 
      propertyName: 'Green Valley',
      unitName: 'Villa 12',
      amount: 1500000,
      createdAt: '2024-12-08T14:20:00Z',
      expiresAt: '2024-12-15T14:20:00Z',
      clickedAt: '2024-12-09T09:15:00Z',
      daysInStage: 3
    },
    { 
      assignmentId: 'ASN_003', 
      investorName: 'Raj Malhotra', 
      investorPhone: '+91 98765 33333', 
      investorEmail: 'raj@gmail.com',
      status: 'REGISTERED', 
      propertyName: 'Skyline Heights',
      unitName: 'Unit A-101',
      amount: 500000,
      createdAt: '2024-12-05T11:45:00Z',
      expiresAt: '2024-12-12T11:45:00Z',
      registeredAt: '2024-12-06T16:30:00Z',
      daysInStage: 5
    },
    { 
      assignmentId: 'ASN_004', 
      investorName: 'Priya Singh', 
      investorPhone: '+91 98765 44444', 
      investorEmail: 'priya@gmail.com',
      status: 'KYC_DONE', 
      propertyName: 'Metro Square',
      unitName: 'Office 301',
      amount: 2000000,
      createdAt: '2024-12-01T09:00:00Z',
      expiresAt: '2024-12-08T09:00:00Z',
      kycCompletedAt: '2024-12-03T14:00:00Z',
      daysInStage: 7
    },
    { 
      assignmentId: 'ASN_005', 
      investorName: 'Vikram Patel', 
      investorPhone: '+91 98765 55555', 
      investorEmail: 'vikram@gmail.com',
      status: 'CONVERTED', 
      propertyName: 'Skyline Heights',
      unitName: 'Unit A-102',
      amount: 1000000,
      createdAt: '2024-11-25T10:00:00Z',
      convertedAt: '2024-11-30T15:30:00Z',
      commission: 40000
    },
    { 
      assignmentId: 'ASN_006', 
      investorName: 'Anjali Desai', 
      investorPhone: '+91 98765 66666', 
      investorEmail: 'anjali@gmail.com',
      status: 'PENDING', 
      propertyName: 'Green Valley',
      unitName: 'Villa 12',
      amount: 750000,
      createdAt: '2024-12-11T08:15:00Z',
      expiresAt: '2024-12-18T08:15:00Z',
      daysInStage: 1
    },
    { 
      assignmentId: 'ASN_007', 
      investorName: 'Suresh Kumar', 
      investorPhone: '+91 98765 77777', 
      investorEmail: 'suresh@gmail.com',
      status: 'CLICKED', 
      propertyName: 'Metro Square',
      unitName: 'Office 302',
      amount: 1500000,
      createdAt: '2024-12-07T16:45:00Z',
      expiresAt: '2024-12-14T16:45:00Z',
      clickedAt: '2024-12-08T11:00:00Z',
      daysInStage: 4
    },
    { 
      assignmentId: 'ASN_008', 
      investorName: 'Neha Gupta', 
      investorPhone: '+91 98765 88888', 
      investorEmail: 'neha@gmail.com',
      status: 'CONVERTED', 
      propertyName: 'Green Valley',
      unitName: 'Villa 08',
      amount: 2000000,
      createdAt: '2024-11-20T12:00:00Z',
      convertedAt: '2024-11-28T10:00:00Z',
      commission: 80000
    },
  ],
};

export default {
  MOCK_BROKER_PROPERTIES,
  MOCK_BROKER_STATS,
  MOCK_BROKER_PIPELINE,
  getPropertySummary,
};
